import { memo, useState, type FC } from 'react'
import ClipContainer from './ClipContainer'
import type { ContextMenuState } from '../../../common/ContextMenu.tsx'
import ContextMenu from '../../../common/ContextMenu.tsx'
import { createPortal } from 'react-dom'
import Icon from '../../../common/Icon.tsx'
import { deleteClip } from '@/features/clips/domain/use-cases/delete-clip.ts'
import { downloadClip } from '@/utils/download-clip.ts'
import { movePlaybackLineToClick } from '@/shared/store/use-cases/move-playback-line-to-click.ts'
import { cutClip } from '@/shared/store/use-cases/cut-clip.ts'
import ConfirmationDialog from '../../../common/ConfirmationDialog.tsx'
import type { Clip } from '@/features/clips/domain/models/clip.ts'
import useUiStore from '@/shared/store/ui.store'
import { useTracking } from '@/providers/TrackingProvider.tsx'

interface VoiceClipProps {
  clip: Clip
}

const VoiceClip: FC<VoiceClipProps> = ({ clip }) => {
  const { trackEvent } = useTracking()
  const [isConfirmingCut, setIsConfirmingCut] = useState(false)
  const openSpeechDialogForClip = useUiStore(
    (state) => state.openSpeechDialogForClip
  )

  const [contextMenuState, setContextMenuState] = useState<ContextMenuState>({
    isOpen: false,
    position: { left: 0, top: 0 },
  })

  const onCutClip = () => {
    setIsConfirmingCut(true)
    return
  }

  const onConfirmClipCut = (confirmation: boolean) => {
    setIsConfirmingCut(false)
    if (confirmation) cutClip(clip)
  }

  return (
    <div
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
        movePlaybackLineToClick(e)
        setContextMenuState({
          isOpen: true,
          position: { left: e.clientX + 2, top: e.clientY },
        })
      }}
    >
      {isConfirmingCut && (
        <ConfirmationDialog
          onClose={onConfirmClipCut}
          title="ls_voice_editing_dialog_title"
          message="ls_voice_editing_dialog_message"
          acceptText="ls_voice_editing_dialog_accept"
        />
      )}
      <ClipContainer clip={clip} />
      {contextMenuState.isOpen &&
        createPortal(
          <ContextMenu
            isOpen={contextMenuState.isOpen}
            setIsOpen={(isOpen: boolean) =>
              setContextMenuState((prev) => ({ ...prev, isOpen }))
            }
            position={contextMenuState.position}
            viewModel={[
              {
                key: 'Split',
                action: onCutClip,
                icon: <Icon name="cut" />,
              },
              {
                key: 'Edit Script',
                action: () => openSpeechDialogForClip(clip),
                icon: <Icon name="subtitles" />,
              },
              {
                key: 'Download',
                action: () => downloadClip(clip, trackEvent),
                icon: <Icon name="download" fill="white" />,
              },
              {
                key: 'Delete',
                action: () => deleteClip(clip),
                icon: <Icon name="xMark" />,
              },
            ]}
          />,
          document.body
        )}
    </div>
  )
}

export default memo(VoiceClip)
